import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useTraduzioni } from '../../i18n';
import { messaggioErrore } from '../accesso/campi';
import { chiavePeriodo, scelteConAttuale } from './CaricamentoMultiplo';
import { CATALOGO, CATEGORIE, etichettaPeriodo, sceltePeriodo } from '../../utils/documenti';
import { adessoRoma } from '../../utils/percorso';

// «Modifica» su OGNI documento già caricato (backend/modifica_documenti.py, PUT /api/documenti/{id}): tipo, di chi è,
// periodo, scadenza, nota e — solo per lo staff — «solo per noi» (il documento sparisce dalla pagina del cliente).
// Si innesta in una riga dei documenti:
//   <MenuModifica documento={voce} clienteId={clienteId} comeStaff persone={persone} tipi={tipi} onFatto={rileggi} />
// Parte chiuso: il bottone apre il pannello, già compilato con i valori di adesso. Si manda solo quello che cambia.
// Il pannello non è mai più largo di quello che lo contiene (sul telefono, a 390 px, sbordava): va messo in un punto
// della riga largo quanto la riga, non in una colonnina di bottoni.

const STILI = {
  // stesse classi del riepilogo del caricamento (components/cliente/documenti.css): niente stili nuovi da inventare
  cliente: {
    bottone: 'btn sm', pannello: 'card r lotto', riga: 'lotto-riga', etichetta: 'meta',
    campo: 'lotto-select', nota: 'lotto-select', piede: 'lotto-piede', principale: 'btn sm p',
    errore: 'rifiuto', spunta: 'meta',
  },
  staff: {
    bottone: 'text-xs px-3 py-1.5 rounded-md border border-gold/30 text-white hover:border-gold/60',
    pannello: 'rounded-xl border border-gold/40 bg-card p-3 space-y-2 mt-2',
    riga: 'flex flex-wrap items-center gap-2', etichetta: 'text-xs text-muted-foreground min-w-[92px]',
    campo: 'flex-1 min-w-[150px] bg-card border border-gold/30 rounded-md px-2 py-1 text-xs text-white',
    nota: 'flex-1 min-w-[200px] bg-card border border-gold/30 rounded-md px-2 py-1 text-xs text-white',
    piede: 'flex flex-wrap items-center justify-end gap-2 pt-1',
    principale: 'text-xs px-3 py-1.5 rounded-md bg-gold text-black font-semibold',
    errore: 'block text-xs text-red-300', spunta: 'flex items-center gap-2 text-xs text-muted-foreground',
  },
};

// I tipi che si possono scegliere: quelli passati dalla pagina, altrimenti il catalogo; al cliente solo i suoi
export const tipiScegliibili = (tipi, comeStaff) => (tipi && tipi.length ? tipi : CATALOGO.tipi)
  .filter((t) => comeStaff || (t.visibile_cliente !== false && (t.chi_carica || 'cliente') === 'cliente'));

// Di chi è il documento: l'id della persona. I documenti di prima hanno solo il nome (persona): si ritrova l'id
export const personaDelDocumento = (doc, persone) => {
  if (doc.persona_id) return doc.persona_id;
  const nome = String(doc.persona || 'titolare').trim();
  if (!nome || nome === 'titolare') return 'titolare';
  return (persone.find((p) => String(p.nome || '').trim().toLowerCase() === nome.toLowerCase()) || {}).id || 'titolare';
};

// Le persone della pratica ({id, nome, etichetta}); la scheda dello staff le passa già pronte per un menu
// ({valore, etichetta}): vanno bene tutte e due
export const personeDelMenu = (persone) => (persone || [])
  .map((p) => (p.id ? p : { id: p.valore, nome: p.etichetta, documenti: p.documenti }))
  .filter((p) => p.id);

const nomeCorto = (p, titolare) => String(p.nome || '').trim().split(/\s+/)[0] || titolare;
// "Oleksandr (titolare)", "Olena (coniuge)" — l'etichetta la manda il server, già nella lingua giusta
const vocePersona = (p, titolare) => (p.etichetta ? `${nomeCorto(p, titolare)} (${p.etichetta})` : String(p.nome || '').trim() || titolare);

// `etichetta`: quando in una riga ci sono più file dello stesso documento (tre buste paga), il bottone dice quale
// ("Modifica luglio 2026"), se no dice solo "Modifica"
const MenuModifica = ({ documento, clienteId, comeStaff = false, persone = [], tipi = [], etichetta = null, onFatto }) => {
  const { t, l: lingua } = useTraduzioni(); // lo staff non passa da FornitoreLingua: resta in italiano
  const c = STILI[comeStaff ? 'staff' : 'cliente'];
  const doc = documento || {};
  const chi = useMemo(() => personeDelMenu(persone).filter((p) => p.id === 'titolare' || p.documenti !== false), [persone]);
  const [aperto, setAperto] = useState(false);
  const [tipo, setTipo] = useState(doc.tipo || '');
  const [persona, setPersona] = useState(() => personaDelDocumento(doc, personeDelMenu(persone)));
  const [periodo, setPeriodo] = useState(chiavePeriodo(doc.periodo));
  const [scadenza, setScadenza] = useState(doc.scadenza || '');
  const [nota, setNota] = useState(doc.note || '');
  const [soloStaff, setSoloStaff] = useState(Boolean(doc.solo_staff));
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState(null);

  const scelteTipi = useMemo(() => tipiScegliibili(tipi, comeStaff), [tipi, comeStaff]);
  const info = scelteTipi.find((t) => t.id === tipo);
  const regola = (info?.periodico || {}).regola;
  // cambiando tipo il periodo cambia forma (mese → trimestre): le scelte sono quelle del tipo scelto ora, e il
  // periodo già salvato resta nell'elenco solo finché il tipo è ancora il suo
  const scelte = useMemo(() => {
    if (!regola) return [];
    const base = sceltePeriodo(regola, adessoRoma(), lingua);
    return tipo === doc.tipo ? scelteConAttuale(base, doc.periodo, etichettaPeriodo(doc.periodo, doc.periodo_testo, lingua)) : base;
  }, [regola, tipo, doc.tipo, doc.periodo, doc.periodo_testo, lingua]);
  const conScadenza = Boolean(info?.scadenza) || Boolean(doc.scadenza);

  const apri = () => {
    setTipo(doc.tipo || '');
    setPersona(personaDelDocumento(doc, chi));
    setPeriodo(chiavePeriodo(doc.periodo));
    setScadenza(doc.scadenza || '');
    setNota(doc.note || '');
    setSoloStaff(Boolean(doc.solo_staff));
    setErrore(null);
    setAperto(true);
  };

  // Solo quello che cambia davvero: quello che non si manda resta com'è (il server non lo tocca)
  const daMandare = () => {
    const corpo = {};
    if (tipo && tipo !== (doc.tipo || '')) corpo.tipo = tipo;
    if (persona !== personaDelDocumento(doc, chi)) corpo.persona_id = persona;
    const scelto = scelte.find((s) => chiavePeriodo(s.periodo) === periodo);
    if (periodo !== chiavePeriodo(doc.periodo) && (scelto || !periodo)) corpo.periodo = scelto ? scelto.periodo : null;
    if (conScadenza && scadenza !== (doc.scadenza || '')) corpo.scadenza = scadenza;
    if (nota.trim() !== (doc.note || '').trim()) corpo.note = nota.trim();
    if (comeStaff && soloStaff !== Boolean(doc.solo_staff)) corpo.solo_staff = soloStaff;
    return corpo;
  };

  const salva = async () => {
    const corpo = daMandare();
    if (!Object.keys(corpo).length) { setAperto(false); return; }
    setAttesa(true);
    setErrore(null);
    try {
      await axios.put(`${API}/documenti/${doc.id}`, corpo);
      toast.success(t('documenti.modifica.salvata'));
      setAperto(false);
      if (onFatto) await onFatto();
    } catch (e) {
      setErrore(messaggioErrore(e, t('documenti.modifica.non_salvata'), lingua));
    } finally { setAttesa(false); }
  };

  if (!aperto) {
    return (
      <button type="button" className={c.bottone} onClick={apri} data-testid="apri-modifica"
        aria-label={t(etichetta ? 'documenti.modifica.aria_etichetta' : 'documenti.modifica.aria',
          { nome: doc.tipo_nome || t('documenti.modifica.il_documento'), etichetta })}>
        {etichetta ? t('documenti.modifica.bottone_etichetta', { etichetta }) : t('documenti.modifica.bottone')}
      </button>
    );
  }
  return (
    <div className={c.pannello} data-testid="modifica-documento" data-cliente={clienteId}
      style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      <div className={c.riga}>
        <span className={c.etichetta}>{t('documenti.lotto.che_documento')}</span>
        <select className={c.campo} aria-label={t('documenti.lotto.che_documento')} value={tipo} disabled={attesa}
          onChange={(e) => { setTipo(e.target.value); setPeriodo(''); }}>
          <option value="">{t('documenti.modifica.da_classificare')}</option>
          {Object.keys(CATEGORIE).map((cat) => (
            scelteTipi.some((t) => t.categoria === cat) && (
              <optgroup key={cat} label={CATEGORIE[cat]}>
                {scelteTipi.filter((t) => t.categoria === cat).map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}{comeStaff && t.visibile_cliente === false ? ' (solo per noi)' : ''}</option>
                ))}
              </optgroup>
            )
          ))}
        </select>
      </div>
      {chi.length > 1 && (
        <div className={c.riga}>
          <span className={c.etichetta}>{t('documenti.lotto.di_chi')}</span>
          <select className={c.campo} aria-label={t('documenti.lotto.di_chi')} value={persona} disabled={attesa}
            onChange={(e) => setPersona(e.target.value)}>
            {chi.map((p) => <option key={p.id} value={p.id}>{vocePersona(p, t('documenti.lotto.titolare'))}</option>)}
          </select>
        </div>
      )}
      {scelte.length > 0 && (
        <div className={c.riga}>
          <span className={c.etichetta}>{t('documenti.lotto.periodo')}</span>
          <select className={c.campo} aria-label={t('documenti.lotto.periodo')} value={periodo} disabled={attesa}
            onChange={(e) => setPeriodo(e.target.value)}>
            <option value="">{t('documenti.periodo.nessuno')}</option>
            {scelte.map((s) => <option key={chiavePeriodo(s.periodo)} value={chiavePeriodo(s.periodo)}>{s.etichetta}</option>)}
          </select>
        </div>
      )}
      {conScadenza && (
        <div className={c.riga}>
          <span className={c.etichetta}>{t('documenti.modifica.scadenza')}</span>
          <input type="date" className={c.campo} aria-label={t('documenti.modifica.scadenza')} value={scadenza} disabled={attesa}
            onChange={(e) => setScadenza(e.target.value)} />
        </div>
      )}
      <div className={c.riga}>
        <span className={c.etichetta}>{t('documenti.modifica.nota')}</span>
        <input type="text" className={c.nota} aria-label={t('documenti.modifica.nota')} value={nota} maxLength={500} disabled={attesa}
          placeholder={t('documenti.modifica.nota_placeholder')} onChange={(e) => setNota(e.target.value)} />
      </div>
      {comeStaff && (
        <label className={c.spunta}>
          <input type="checkbox" checked={soloStaff} disabled={attesa} aria-label="Solo per noi"
            onChange={(e) => setSoloStaff(e.target.checked)} />
          Solo per noi (il cliente non lo vede)
        </label>
      )}
      {errore && <span className={c.errore} role="alert">{errore}</span>}
      <div className={c.piede}>
        <button type="button" className={c.bottone} onClick={() => setAperto(false)} disabled={attesa}>{t('comune.annulla')}</button>
        <button type="button" className={c.principale} onClick={salva} disabled={attesa}>{t('comune.salva')}</button>
      </div>
    </div>
  );
};

export default MenuModifica;
